import { useSearchParams } from 'react-router-dom';
import React, { useState, useEffect, SyntheticEvent } from 'react';

import { Stack } from '@mui/system';
import {
  Box,
  Tab,
  Card,
  Tabs,
  Divider
} from '@mui/material';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';

import { useFeatures } from 'src/sections/billing/usage/hooks/use-features';
import BrandSettingsCard from 'src/sections/admin/organization/settings/components/brand-settings';
import BusinessSettingsCard from 'src/sections/admin/organization/settings/components/business-settings';
import ReferenceSettingsCard from 'src/sections/admin/organization/settings/components/reference-settings';
import CustomFieldSettingsCard from 'src/sections/admin/organization/settings/components/custom-field-settings';
import JobApplicationSettingsCard from 'src/sections/admin/organization/settings/components/job-application-settings';
import LabelSettingsCard from 'src/sections/admin/organization/settings/components/label-settings';
import AiSettingsCard from 'src/sections/admin/organization/settings/components/ai-settings';

import { TenantType } from 'src/types/enums';
import { FeatureType } from 'src/types/subscription.types';

import JobBoardCard from '../components/job-board-card';
import SubTenantCard from '../components/sub-tenant-card';
import ExportSettingsCard from '../components/export-settings';
import EmailSyncSettingsCard from '../components/email-sync-settings';
import WorkingHoursComponent from '../components/working-hour-selector';
import DealFLowPipelineSettingsCard from '../components/deal-flow-pipeline-settings';
import EmailActivitiesCard from '../components/email-activities';
import ResourceExportCard from '../components/resource-export-card';

interface TabPanelProps {
  children?: React.ReactNode;
  identifier: TabValue;
  value: TabValue;
}

function TabPanel(props: TabPanelProps) {

  const { children, value, identifier, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== identifier}
      id={`settings-tabpanel-${identifier}`}
      aria-labelledby={`settings-tab-${identifier}`}
      {...other}
    >
      {value === identifier && (
        <Box sx={{ py: 1 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// helper function for a11y props
function a11yProps(index: number) {
  return {
    id: `settings-tab-${index}`,
    'aria-controls': `settings-tabpanel-${index}`,
  };
}

enum TabValue {
  Business = 'business',
  Brand = 'brand',
  SubOrg = 'sub-org',
  JobBoard = 'job-board',
  References = 'references',
  JobApplications = 'job-applications',
  DealFlow = 'deal-flow',
  EmailSync = 'email-sync',
  CustomFields = 'custom-fields',
  Labels = 'labels',
  AiSettings = 'ai-settings',
}

export default function OrganizationSettingsView() {
  const { t } = useTranslate();
  const tenantType = useOrgTenant();
  const { canAccess } = useFeatures();

  const [searchParams, setSearchParams] = useSearchParams();

  const [value, setValue] = useState<TabValue>(TabValue.Brand);

  useEffect(() => {
    const tab = searchParams.get('tab') as TabValue;
    if (tab) {
      setValue(tab);
    }
  }, [searchParams]);

  const handleChange = (_event: SyntheticEvent, newValue: TabValue) => {
    setValue(newValue);
    setSearchParams({ tab: newValue });
  };

  const isRecruiter = tenantType === TenantType.Recruiter;
  const canAccessSubOrg = [TenantType.Recruiter, TenantType.Client].includes(tenantType) && canAccess(FeatureType.SUB_ORG_ENABLED);
  const canAccessJobBoard = [TenantType.Recruiter, TenantType.Client].includes(tenantType);
  const canAccessDealFLow = [TenantType.Recruiter, TenantType.Client].includes(tenantType);
  const canAccessEmailSync = [TenantType.Recruiter, TenantType.Client].includes(tenantType) && canAccess(FeatureType.EMAIL_CONNECTOR_ENABLED);
  const canAccessCustomFields = [TenantType.Recruiter, TenantType.Client].includes(tenantType);
  const canAccessLabels = [TenantType.Recruiter, TenantType.Client].includes(tenantType);
  const canAccessAiSettings = [TenantType.Recruiter, TenantType.Client].includes(tenantType);

  return (
    <Card sx={{ px: 2, pt: 1 }}>
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="organization settings tabs"
          >
            <Tab label={t('admin-organization.settings.tabs.brand')} {...a11yProps(1)} value={TabValue.Brand} />
            <Tab label={t('admin-organization.settings.tabs.business')} {...a11yProps(2)} value={TabValue.Business} />
            {canAccessSubOrg && <Tab label={t('admin-organization.settings.tabs.sub_org')} {...a11yProps(3)} value={TabValue.SubOrg} />}
            {canAccessJobBoard && <Tab label={t('admin-organization.settings.tabs.job_board')} {...a11yProps(4)} value={TabValue.JobBoard} />}
            <Tab label={t('admin-organization.settings.tabs.references')} {...a11yProps(1)} value={TabValue.References} />
            <Tab label={t('admin-organization.settings.tabs.job_applications')} {...a11yProps(1)} value={TabValue.JobApplications} />
            {canAccessDealFLow && <Tab label={t('admin-organization.settings.tabs.deal_flow')} {...a11yProps(2)} value={TabValue.DealFlow} />}
            {canAccessEmailSync && <Tab label={t('admin-organization.settings.tabs.email_sync')} {...a11yProps(1)} value={TabValue.EmailSync} />}
            {canAccessCustomFields && <Tab label={t('admin-organization.settings.tabs.custom_fields')} {...a11yProps(1)} value={TabValue.CustomFields} />}
            {canAccessLabels && <Tab label={t('admin-organization.settings.tabs.labels')} {...a11yProps(1)} value={TabValue.Labels} />}
            {canAccessAiSettings && <Tab label="AI Settings" {...a11yProps(1)} value={TabValue.AiSettings} />}
          </Tabs>
        </Box>

        {/* business settings tab */}
        <TabPanel value={value} identifier={TabValue.Business}>
          <Stack spacing={2}>

            {isRecruiter && (
              <>
                <BusinessSettingsCard />
                <Divider />
                <WorkingHoursComponent />
                <Divider />
              </>
            )}

            <ResourceExportCard />
          </Stack>
        </TabPanel>


        <TabPanel value={value} identifier={TabValue.Brand}>
          <BrandSettingsCard />
          <Divider />
          <ExportSettingsCard />
        </TabPanel>

        {/* sub-organization tab - only if feature is enabled */}
        {canAccessSubOrg && (
          <TabPanel value={value} identifier={TabValue.SubOrg}>
            <SubTenantCard />
          </TabPanel>
        )}

        {/* job Board tab - only if feature is enabled  */}
        {canAccessJobBoard && (
          <TabPanel value={value} identifier={TabValue.JobBoard}>
            <JobBoardCard />
          </TabPanel>
        )}

        <TabPanel value={value} identifier={TabValue.References}>
          <ReferenceSettingsCard />
        </TabPanel>

        <TabPanel value={value} identifier={TabValue.JobApplications}>
          <JobApplicationSettingsCard />
        </TabPanel>

        <TabPanel value={value} identifier={TabValue.DealFlow}>
          <DealFLowPipelineSettingsCard />
        </TabPanel>

        {
          canAccessEmailSync && (
            <TabPanel value={value} identifier={TabValue.EmailSync}>
              <EmailSyncSettingsCard />
              <EmailActivitiesCard />
            </TabPanel>
          )}

        {
          canAccessCustomFields && (
            <TabPanel value={value} identifier={TabValue.CustomFields}>
              <CustomFieldSettingsCard />
            </TabPanel>
          )
        }

        {
          canAccessLabels && (
            <TabPanel value={value} identifier={TabValue.Labels}>
              <LabelSettingsCard />
            </TabPanel>
          )
        }

        {
          canAccessAiSettings && (
            <TabPanel value={value} identifier={TabValue.AiSettings}>
              <AiSettingsCard />
            </TabPanel>
          )
        }

      </Box>
    </Card>
  );
}